<script setup lang="ts">
import { ref, computed } from 'vue';
import { useInstitutionStore } from '@/stores/institution';
import { capitalize } from 'lodash';

const institutionStore = useInstitutionStore();
const emit = defineEmits(['onChange']);

interface Props {
  keyboardClass?: string;
  placeholder?: string;
  title?: string;
}

withDefaults(defineProps<Props>(), {
  keyboardClass: 'keyboard-simple',
  placeholder: 'global.description',
});

const input = ref<string>('');
const lastKeyPress = ref<string>('');
const upperCase = ref<boolean>(true);

const locale = computed(() =>
  institutionStore?.locale === 'de' ? 'de' : 'da'
);

const NUMBERS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

const KEYBOARD_LAYOUTS: Record<string, string[][]> = {
  de: [
    [...NUMBERS, 'ß'],
    ['q', 'w', 'e', 'r', 't', 'z', 'u', 'i', 'o', 'p', 'ü'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'ö', 'ä'],
    ['shift', 'y', 'x', 'c', 'v', 'b', 'n', 'm', 'bksp'],
    ['@', 'space', '?'],
  ],
  da: [
    NUMBERS,
    ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p', 'å'],
    ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'æ', 'ø'],
    ['shift', 'z', 'x', 'c', 'v', 'b', 'n', 'm', 'bksp'],
    ['@', 'space', '?'],
  ],
};

const ROWS = computed(() => KEYBOARD_LAYOUTS[locale.value]);

const getSize = (key: string) => {
  let width = 68;

  if (key === 'shift' || key === 'bksp') width = 106;
  if (key === 'space') width = 455;

  return {
    width: `${width}px`,
    height: '68px',
  };
};

const backspaceInterval = ref<ReturnType<typeof setInterval> | null>(null);
const backspaceDelay = 100;

function deleteCharacter() {
  input.value = input.value.slice(0, -1);
  if (input.value.length === 0) upperCase.value = true;
  emit('onChange', input.value);
}

function startBackspaceInterval() {
  if (backspaceInterval.value === null) {
    deleteCharacter();
    backspaceInterval.value = setInterval(() => {
      deleteCharacter();
    }, backspaceDelay);
  }
}

function stopBackspaceInterval() {
  if (backspaceInterval.value !== null) {
    clearInterval(backspaceInterval.value);
    backspaceInterval.value = null;
  }
}

function onKeyPress(key: string) {
  lastKeyPress.value = key;

  if (key === 'shift') {
    upperCase.value = !upperCase.value;
    return;
  }

  if (key === 'bksp') {
    deleteCharacter();
    return;
  }

  if (key === 'space') {
    input.value += ' ';
    emit('onChange', input.value);
    return;
  }

  if (key === 'ß') {
    input.value += upperCase.value ? 'ẞ' : 'ß';
  } else {
    input.value += upperCase.value ? capitalize(key) : key;
  }
  if (input.value.length === 1) upperCase.value = false;
  emit('onChange', input.value);
}
</script>

<template>
  <div>
    <p class="h800 mb-4" v-if="title">{{ title }}</p>
    <input
      :value="input"
      class="w-full input border-b-2 border-darkNeutral-600A mb-12 text-6xl pb-2 font-semibold bg-neutral-100 focus:outline-none select-none"
      :placeholder="$t(placeholder)"
    />
    <div
      v-for="(row, index) in ROWS"
      :key="index"
      class="flex items-center justify-center mb-4"
    >
      <div
        v-for="(key, index) in row"
        :key="index"
        class="flex rounded rounded-3xl border-1 border-neutral-1000 h900 items-center justify-center select-none"
        :class="[
          index !== row.length - 1 ? 'mr-3' : '',
          key === 'shift' && upperCase == true
            ? 'bg-neutral-600 text-neutral-0'
            : 'bg-neutral-0 text-neutral-1000',
        ]"
        :style="getSize(key)"
        @click="onKeyPress(key)"
        @touchstart="key === 'bksp' && startBackspaceInterval()"
        @touchend="key === 'bksp' && stopBackspaceInterval()"
        @touchcancel="key === 'bksp' && stopBackspaceInterval()"
      >
        <fa-icon
          v-if="key === 'shift'"
          :icon="['fas', 'fa-up']"
          class="text-4xl"
        />

        <fa-icon
          v-else-if="key === 'bksp'"
          :icon="['far', 'fa-delete-left']"
          class="text-4xl"
        />

        <p v-else-if="key === 'space'">{{ key }}</p>
        <p v-else>{{ upperCase ? capitalize(key) : key }}</p>
      </div>
    </div>
  </div>
</template>

<style>
input::placeholder {
  color: rgba(21, 21, 21, 0.1);
}
</style>
